import React, { useCallback } from "react";
import { gql, useQuery } from "@apollo/client";
import {
  CommunityWithSpend,
  FindAllMyCommunitySpendQuery,
  FindAllMyCommunitySpendQueryVariables,
} from "../../../../schemaTypes";

export const findAllMyCommunitySpendQuery = gql`
  query FindAllMyCommunitySpend(
    $page: Int!
    $pageSize: Int!
    $searchText: String
    $sortBy: String
    $sortOrder: String
  ) {
    findAllMyCommunitySpend(
      page: $page
      pageSize: $pageSize
      searchText: $searchText
      sortBy: $sortBy
      sortOrder: $sortOrder
    ) {
      data {
        community {
          id
          name
          alias
          logoUrl
          createdAt
          updatedAt
        }
        totalSpend
      }
      total
      page
      pageSize
      totalPages
    }
  }
`;

export interface WithFindAllMyCommunitySpend {
  communitiesWithSpend: CommunityWithSpend[];
  loading: boolean;
  loadMore: () => void;
  hasMore: boolean;
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
}

interface Props {
  variables: FindAllMyCommunitySpendQueryVariables;
  children: (data: WithFindAllMyCommunitySpend) => React.ReactElement | null;
}

export const FindAllMyCommunitySpend: React.FC<Props> = ({
  children,
  variables,
}) => {
  const { data, loading, fetchMore } = useQuery<
    FindAllMyCommunitySpendQuery,
    FindAllMyCommunitySpendQueryVariables
  >(findAllMyCommunitySpendQuery, {
    variables,
    notifyOnNetworkStatusChange: true,
  });

  const pageData = data?.findAllMyCommunitySpend || {
    data: [],
    total: 0,
    page: variables.page,
    pageSize: variables.pageSize,
    totalPages: 0,
  };

  const communitiesWithSpend = pageData.data;
  const page = pageData.page;
  const pageSize = pageData.pageSize;
  const total = pageData.total;
  const totalPages = pageData.totalPages;
  const hasMore = page < totalPages;

  const loadMore = useCallback(() => {
    if (!hasMore) return;
    fetchMore({
      variables: {
        ...variables,
        page: page + 1,
      },
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult || !fetchMoreResult.findAllMyCommunitySpend) {
          return prev;
        }
        return {
          ...fetchMoreResult,
          findAllMyCommunitySpend: {
            ...fetchMoreResult.findAllMyCommunitySpend,
            data: [
              ...(prev?.findAllMyCommunitySpend?.data || []),
              ...(fetchMoreResult.findAllMyCommunitySpend.data || []),
            ],
          },
        };
      },
    });
  }, [fetchMore, variables, page, hasMore]);

  return children({
    communitiesWithSpend: communitiesWithSpend as any[],
    loading,
    loadMore,
    hasMore,
    page,
    pageSize,
    total,
    totalPages,
  });
};
